import { NUMBER_PRECISION } from 'constants.js'
import type { Translate } from 'types'
import { noop } from 'utils'
/* eslint-disable no-restricted-globals */

export const formatValidNumber = (number?: number, suffix = '', prefix = '') => {
  if (!number && number !== 0) {
    return '-'
  }

  if (prefix || suffix) {
    return `${prefix}${number}${suffix}`
  }

  return number
}

export const handleNullCase = (number: number | null) => (number === null ? '-' : number)

interface FormatPreciseValue {
  number?: number | string | null
  precision?: number
  translate?: Translate
}

export const formatPreciseValue = ({ number, precision, translate = noop }: FormatPreciseValue) => {
  const stringifiedNumber = `${number || ''}`

  if (number === null || isNaN(parseFloat(stringifiedNumber))) {
    return number
  }

  const defaultPrecision = stringifiedNumber.includes('.') ? stringifiedNumber.split('.')[1].length : NUMBER_PRECISION
  const appliedPrecision = typeof precision === 'number' ? precision : defaultPrecision
  const toLocaleOptions = {
    minimumFractionDigits: appliedPrecision,
    maximumFractionDigits: appliedPrecision,
  }

  return parseFloat(stringifiedNumber).toLocaleString(translate('numberLocale'), toLocaleOptions)
}

export const getPercentDifference = (compared: number, current: number, precision: number, handleNull = false) => {
  const percent = getPercentDifferenceAsNumber(compared, current, precision)

  if (!percent) {
    return {
      value: handleNull && compared === null ? '-' : '0%',
      type: 'zero',
    }
  }

  if (percent > 0) {
    return {
      value: `${percent}%`,
      type: 'positive',
    }
  }

  return {
    value: `${percent}%`,
    type: 'negative',
  }
}

export const getDifference = (compared: number, current: number) => {
  const difference = formatPreciseValue({
    number: getDifferenceAsNumber(compared, current),
  })

  if (!difference) {
    return {
      value: '0',
      type: 'zero',
    }
  }

  if (difference && +difference > 0) {
    return {
      value: `+${difference}`,
      type: 'positive',
    }
  }

  return {
    value: `${difference}`,
    type: 'negative',
  }
}

export const getDifferenceAsNumber = (compared: number, current: number) => {
  if (!isNaN(compared) && !isNaN(current) && compared > 0 && current > 0) {
    return current - compared
  }

  return null
}

export const getPercentDifferenceAsNumber = (compared: number, current: number, precision: number) => {
  if (!isNaN(compared) && !isNaN(current) && compared > 0 && current > 0) {
    const result = ((current - compared) / compared) * 100

    return precision ? parseFloat(`${result || ''}`).toFixed(precision) : Math.round(result)
  }

  return null
}

interface GetDifferenceWithPrecision {
  handleNullCase?: boolean
  number?: number | null
  precision?: number
  suffix?: string
  translate: Translate
}

export const getDifferenceWithPrecision = ({
  handleNullCase: handleNull = false,
  number,
  precision = 2,
  suffix = '%',
  translate,
}: GetDifferenceWithPrecision) => {
  const fixedNumber = number || number === 0 ? number * 100 : number
  const preciseNumber = formatPreciseValue({
    number: fixedNumber,
    precision,
    translate,
  })

  if (!number) {
    return {
      value: handleNull && (number === null || number === undefined) ? '-' : `0${suffix}`,
      type: 'zero',
    }
  }

  if (fixedNumber && fixedNumber > 0) {
    return {
      value: `${preciseNumber}${suffix}`,
      type: 'positive',
    }
  }

  return {
    value: `${preciseNumber}${suffix}`,
    type: 'negative',
  }
}

export const roundDownNumber = (number: number) => Math.floor(number * 100) / 100

export const thousandsSeparator = (number?: number | null, separator = ' ') => {
  if (number === null || number === undefined || isNaN(number)) return 0

  const stringNumberParts = number.toString().split('.')
  const integer = stringNumberParts[0]
    .split('')
    .reverse()
    .map((element, index) => (index % 3 === 0 && index !== 0 ? element + separator : element))
    .reverse()
    .join('')

  return stringNumberParts.length > 1 ? `${integer}.${stringNumberParts[1]}` : integer
}

export const sumBy = (array: number[], extract = (x: number) => x, from = 0) =>
  array.reduce((prev, next) => prev + extract(next), from)
